import type AttachmentPreviewWellView from 'owa-mail-list-store/lib/store/schema/AttachmentPreviewWellView';
import { getStore } from '../store/Store';
import { getListViewDimensions } from './getListViewDimensions';
import { shouldRenderColumnHeaders } from './shouldRenderColumnHeaders';

// Container width constants for three-column layout
const LV_LEFT_RIGHT_PADDING = 20;
const WIDTH_PERSONA_ICON = 48;
const SCROLL_BAR_WIDTH = 18;
// Hardcoded container width for multi-column layout (MCL --> MailListItemSingleLineView)
const MCL_CONTAINER_WIDTH = 500;

// Per-item width allocations (must match values in MailListItemRichPreviewWell.scss)
export const WIDTH_ALLOC_FOR_DOC: number = 136; // 132: width + 4: margin-right
export const WIDTH_ALLOC_FOR_IMAGE: number = 68; // 64: width + 4: margin-right
export const WIDTH_OF_PLUS_MORE_DIV: number = 34;

export interface PreviewWellViewState {
    shouldShowPlusMore: boolean;
    numDocToShow: number;
    numImageToShow: number;
}

/**
 * Returns true when only document previews are shown (no images),
 * which means the preview well should use the shorter row height.
 */
export const isAttachmentPreviewShortHeight = (numImageToShow: number): boolean => {
    return numImageToShow === 0;
};

/**
 * Determines how many doc and image previews fit in the attachment preview well
 * for the current layout, and whether a "+N more" indicator is needed.
 *
 * Container width is resolved internally based on layout mode:
 * - Column headers mode: subject column width minus padding
 * - Multi Column Layout (MCL --> MailListItemSingleLineView): fixed 500px
 * - Single Column Layout (SCL --> MailListItemFullThreeColumn): list view width minus persona icon, padding, and scrollbar
 *
 * @param attachmentPreviewWellView - The attachment preview well view containing doc and image view state IDs
 * @param tableViewId - The current table view (used to detect column-headers mode)
 * @param isWideView - True for single-line view (aka MultiColumnLayout), false for three-column view (aka SingleColumnLayout)
 */
export const calculateAttachmentPreviewWellViewState = (
    attachmentPreviewWellView: AttachmentPreviewWellView,
    tableViewId: string,
    isWideView: boolean
): PreviewWellViewState => {
    const numOfDocs = attachmentPreviewWellView.documentViewStateIds.length;
    const numOfImages = attachmentPreviewWellView.imageViewStateIds.length;
    let containerWidth: number;
    if (shouldRenderColumnHeaders(tableViewId)) {
        containerWidth = (getStore().subjectColumnWidth ?? 0) - 20 /* Left Padding */;
    } else if (isWideView) {
        containerWidth = MCL_CONTAINER_WIDTH;
    } else {
        const { listViewWidth } = getListViewDimensions();
        containerWidth =
            listViewWidth - LV_LEFT_RIGHT_PADDING - WIDTH_PERSONA_ICON - SCROLL_BAR_WIDTH;
    }

    const totalRequiredWidth =
        numOfDocs * WIDTH_ALLOC_FOR_DOC + numOfImages * WIDTH_ALLOC_FOR_IMAGE;

    if (totalRequiredWidth <= containerWidth) {
        return {
            shouldShowPlusMore: false,
            numDocToShow: numOfDocs,
            numImageToShow: numOfImages,
        };
    }

    // Reserve space for the "+N more" pill
    let availableWidth = containerWidth - WIDTH_OF_PLUS_MORE_DIV;

    const numDocsThatCanFit = Math.floor(availableWidth / WIDTH_ALLOC_FOR_DOC);
    const numDocToShow = Math.min(numOfDocs, numDocsThatCanFit);
    availableWidth -= numDocToShow * WIDTH_ALLOC_FOR_DOC;

    const numImagesThatCanFit = Math.floor(availableWidth / WIDTH_ALLOC_FOR_IMAGE);
    const numImageToShow = Math.min(numOfImages, numImagesThatCanFit);

    return {
        shouldShowPlusMore: true,
        numDocToShow,
        numImageToShow,
    };
};
